import React, { useRef, useMemo } from "react";
import { ContentHeader } from "@components";
import DataTable from "../components/datatable/DataTable";
import API from "../api";
import toastr from "toastr";
import useLabBackup from "../hooks/useLabBackup";
import { downloadBlob } from "../utils/downloadFile";

interface Backup {
  id: number;
  filename: string;
  date: string;
  file_size: number;
}

interface TableData {
  data: Backup[];
  total: number;
}

const BackupManager: React.FC = () => {
  const tableRef = useRef<{ refetch: () => void } | null>(null);

  const { labBackingUp, downloadLabBackup } = useLabBackup();

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toISOString().split('T')[0];
  };

  const fetchBackups = async (
    page: number,
    search: string,
    sortBy = "date",
    sortOrder = "desc"
  ): Promise<TableData> => {
    try {
      const res = await API.get(
        `/backup/backupList?page=${page}&pageSize=10&search=${search}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      
      // Validate response structure
      if (!res.data.success) {
        throw new Error(res.data.message || 'API returned success: false');
      }
      
      return {
        data: res.data.data || [],
        total: res.data.total || 0,
      };
    } catch (error: any) {
      console.error('Fetch error:', error);
      toastr.error(`Failed to fetch backups: ${error.response?.data?.message || error.message}`);
      return { data: [], total: 0 };
    }
  };

  const createBackup = async () => {
    if (!confirm('Create new database backup?')) return;

    try {
      const res = await API.post('/backup/createBackup');
      if (res.data.success) {
        toastr.success(res.data.message);
        tableRef.current?.refetch();
      } else {
        toastr.error(res.data.message);
      }
    } catch (err) {
      console.error('Create error:', err);
      toastr.error('Backup creation failed');
    }
  };

  const downloadBackup = async (filename: string) => {
    try {
      const response = await API.get(`/backup/backupDownload/${encodeURIComponent(filename)}`, { responseType: 'blob' });
      downloadBlob(response.data, filename);
    } catch {
      toastr.error('Backup download failed');
    }
  };

  const deleteBackup = async (id: number) => {
    if (!confirm('Delete this backup? This cannot be undone.')) return;

    try {
      const res = await API.delete(`/backup/backupDelete/${id}`);
      if (res.data.success) {
        toastr.success(res.data.message);
        tableRef.current?.refetch();
      } else {
        toastr.error(res.data.message);
      }
    } catch (err) {
      console.error('Delete error:', err);
      toastr.error('Delete failed');
    }
  };

  const columns = useMemo(
    () => [
      { 
        header: "#", 
        id: "index",
        cell: ({ row }: any) => row.original.id 
      },
      { 
        header: "Filename", 
        accessorKey: "filename",
        cell: ({ row }: any) => <code>{row.original.filename}</code>
      },
      { 
        header: "Date", 
        accessorKey: "date",
        cell: ({ row }: any) => formatDate(row.original.date)
      },
      { 
        header: "File Size", 
        accessorKey: "file_size",
        cell: ({ row }: any) => formatFileSize(row.original.file_size)
      },
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }: any) => (
          <div className="d-flex justify-content-center gap-2">
            <button
              className="btn btn-light mr-2"
              title="Download"
              onClick={() => downloadBackup(row.original.filename)}
            >
              Download
            </button>
            <button
              className="btn btn-light"
              title="Delete"
              onClick={() => deleteBackup(row.original.id)}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <ContentHeader title="Database Backups" />
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-primary" onClick={downloadLabBackup} disabled={labBackingUp}>
            <i className="bx bx-cloud-download me-1" aria-hidden="true" />
            {labBackingUp ? 'Preparing Lab Backup…' : 'Lab Backup'}
          </button>
          <button className="btn btn-light" onClick={createBackup}>
            Create Backup
          </button>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchBackups}
                queryKey={["backups"]}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BackupManager;